import { useEffect, useState } from 'react'
import { ApiError, api } from '../api/client'
import type { SharedPublication } from '../api/types'
import { Face, Mark } from '../components/primitives'
import { radius } from '../components/primitives/shape'
import { describeAge } from '../status/status'

/**
 * One published item, for somebody holding its link.
 *
 * Public, and public in the strong sense: it paints for a reader with no
 * account, no organisation and no membership, because holding the token is the
 * whole authorisation. `App` puts this branch above its own session gate for
 * the same reason `/guide` is there, and the page never asks who you are.
 *
 * It carries `noindex`. A capability URL a crawler can reach is not one, and
 * the tag is set here rather than in the document head because the head is
 * shared with every other route.
 *
 * A revoked token and a token that never existed both render the same refusal.
 * The difference is not the reader's business, and saying which would turn the
 * page into an oracle for whether a link was ever real.
 */
export function Shared({ token }: { token: string }) {
  const [state, setState] = useState<
    { status: 'loading' }
    | { status: 'gone' }
    | { status: 'unreachable'; detail: string }
    | { status: 'ready'; publication: SharedPublication }
  >({ status: 'loading' })

  useEffect(() => {
    const tag = document.createElement('meta')
    tag.name = 'robots'
    tag.content = 'noindex, nofollow'
    document.head.appendChild(tag)
    return () => tag.remove()
  }, [])

  useEffect(() => {
    let live = true
    api.sharedPublication(token)
      .then((publication) => live && setState({ status: 'ready', publication }))
      .catch((error) => {
        if (!live) return
        setState(error instanceof ApiError && error.status === 404
          ? { status: 'gone' }
          : { status: 'unreachable', detail: String(error) })
      })
    return () => { live = false }
  }, [token])

  if (state.status === 'loading') {
    return (
      <Frame>
        <p role="status" style={{ color: 'var(--text-dim)' }}>Loading…</p>
      </Frame>
    )
  }

  if (state.status === 'gone') {
    return (
      <Frame>
        <h1 style={heading}>This link does not open anything.</h1>
        <p style={prose}>
          It was either withdrawn by whoever shared it, replaced with a newer
          one, or never a link at all. Ask them for a fresh one.
        </p>
      </Frame>
    )
  }

  if (state.status === 'unreachable') {
    return (
      <Frame>
        <h1 style={heading}>stagepost is not answering.</h1>
        <p style={prose}>This is the server, not your link. Try again shortly.</p>
      </Frame>
    )
  }

  return <Publication publication={state.publication} />
}

const heading = {
  fontFamily: 'var(--font-human)', fontSize: 26, fontWeight: 500,
  margin: '0 0 10px', lineHeight: 1.25,
} as const

const prose = {
  fontFamily: 'var(--font-human)', fontSize: 15, lineHeight: 1.6,
  color: 'var(--text-dim)', margin: 0, maxWidth: '54ch',
} as const

const mono = {
  font: '400 11.5px var(--font-machine)', color: 'var(--text-faint)',
  overflowWrap: 'anywhere',
} as const

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100dvh', background: 'var(--ground)' }}>
      <div style={{ maxWidth: 780, margin: '0 auto', padding: '28px 22px 64px' }}>
        <a
          href="/"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 9, marginBottom: 30, textDecoration: 'none', color: 'var(--text-dim)' }}
        >
          <Mark />
          <span style={{ fontSize: 13.5, fontWeight: 700 }}>stagepost</span>
        </a>
        {children}
      </div>
    </div>
  )
}

/**
 * The item itself.
 *
 * A file picker only where there is more than one file, which is the same rule
 * the Setup pane follows: an instructions tree is eight files and a hook is
 * one, and a picker over a single file is a control that says nothing.
 */
function Publication({ publication }: { publication: SharedPublication }) {
  const [path, setPath] = useState<string | null>(null)
  const shown = publication.files.find((file) => file.path === path)
    ?? publication.files[0] ?? null

  return (
    <Frame>
      <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 22 }}>
        <Face name={publication.display_name} avatarKey={publication.avatar_key} size={38} />
        <div style={{ minWidth: 0 }}>
          <h1 style={{ ...heading, fontSize: 21, margin: 0 }}>{publication.name}</h1>
          <div style={mono}>
            {publication.kind} · shared by {publication.display_name} ·
            published {describeAge(publication.published_at)}
          </div>
        </div>
      </div>

      {publication.files.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
          {publication.files.map((file) => (
            <button
              key={file.path}
              aria-current={file.path === shown?.path}
              onClick={() => setPath(file.path)}
              style={{ font: '400 11px var(--font-machine)', padding: '4px 8px', borderRadius: radius.xs, cursor: 'pointer', overflowWrap: 'anywhere', color: file.path === shown?.path ? 'var(--accent-bright)' : 'var(--text-dim)', background: file.path === shown?.path ? 'var(--accent-wash)' : 'var(--panel-strong)', border: `1px solid ${file.path === shown?.path ? 'var(--accent-edge)' : 'transparent'}` }}
            >
              {file.path}
            </button>
          ))}
        </div>
      )}

      {shown && (
        <pre style={{ margin: 0, padding: 14, maxHeight: '58dvh', overflow: 'auto', borderRadius: radius.sm, background: 'var(--ground-raised)', border: '1px solid var(--border)', font: '400 12px/1.65 var(--font-machine)', color: 'var(--text-dim)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {shown.content}
        </pre>
      )}

      <p style={{ ...prose, fontSize: 13, marginTop: 18 }}>
        Whoever shared this can withdraw the link at any time, and doing so
        stops every copy of it at once.
      </p>
    </Frame>
  )
}
